import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Calendar, MapPin, Clock, Users, Heart, CheckCircle, ChevronLeft, ChevronRight, List, CalendarDays } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DAYS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

const Events = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('timeline');
  const [calMonth, setCalMonth] = useState(new Date().getMonth());
  const [calYear, setCalYear] = useState(new Date().getFullYear());
  const [selectedDate, setSelectedDate] = useState(null);
  const [registering, setRegistering] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [submitted, setSubmitted] = useState({});

  useEffect(() => {
    fetch(`${API}/api/events?status=active`)
      .then(r => r.json())
      .then(data => { setEvents(data.events || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const handleRegister = async (eventId) => {
    if (!form.name || !form.email) { toast.error('Name and email are required'); return; }
    try {
      const res = await fetch(`${API}/api/events/${eventId}/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      const data = await res.json();
      if (!res.ok) { toast.error(data.detail || 'Registration failed'); return; }
      toast.success('Registration successful!');
      setSubmitted(prev => ({ ...prev, [eventId]: true }));
      setRegistering(null);
      setForm({ name: '', email: '', phone: '', message: '' });
      setEvents(prev => prev.map(e => e._id === eventId ? { ...e, registration_count: (e.registration_count || 0) + 1 } : e));
    } catch { toast.error('Network error'); }
  };

  const today = new Date(new Date().toDateString());
  const upcoming = events.filter(e => new Date(e.date) >= today).sort((a, b) => new Date(a.date) - new Date(b.date));
  const past = events.filter(e => new Date(e.date) < today).sort((a, b) => new Date(b.date) - new Date(a.date));

  // Calendar helpers
  const getDaysInMonth = (month, year) => new Date(year, month + 1, 0).getDate();
  const getFirstDayOfMonth = (month, year) => new Date(year, month, 1).getDay();

  const eventsOnDate = (dateStr) => events.filter(e => e.date === dateStr);

  const calendarDays = () => {
    const daysInMonth = getDaysInMonth(calMonth, calYear);
    const firstDay = getFirstDayOfMonth(calMonth, calYear);
    const days = [];
    for (let i = 0; i < firstDay; i++) days.push(null);
    for (let d = 1; d <= daysInMonth; d++) days.push(d);
    return days;
  };

  const formatDateStr = (day) => {
    const m = String(calMonth + 1).padStart(2, '0');
    const d = String(day).padStart(2, '0');
    return `${calYear}-${m}-${d}`;
  };

  const prevMonth = () => { if (calMonth === 0) { setCalMonth(11); setCalYear(y => y - 1); } else setCalMonth(m => m - 1); setSelectedDate(null); };
  const nextMonth = () => { if (calMonth === 11) { setCalMonth(0); setCalYear(y => y + 1); } else setCalMonth(m => m + 1); setSelectedDate(null); };

  const selectedEvents = selectedDate ? eventsOnDate(selectedDate) : [];

  const RegForm = ({ eventId }) => (
    <div className="space-y-3 border-t border-gray-200 pt-4 mt-4" data-testid={`register-form-${eventId}`}>
      <div>
        <Label className="text-xs text-gray-500">Name *</Label>
        <Input value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} placeholder="Your full name" className="mt-1" data-testid="reg-name" />
      </div>
      <div>
        <Label className="text-xs text-gray-500">Email *</Label>
        <Input type="email" value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))} placeholder="you@email.com" className="mt-1" data-testid="reg-email" />
      </div>
      <div>
        <Label className="text-xs text-gray-500">Phone</Label>
        <Input value={form.phone} onChange={e => setForm(f => ({...f, phone: e.target.value}))} placeholder="(555) 123-4567" className="mt-1" data-testid="reg-phone" />
      </div>
      <div>
        <Label className="text-xs text-gray-500">Notes</Label>
        <Input value={form.message} onChange={e => setForm(f => ({...f, message: e.target.value}))} placeholder="Any additional info..." className="mt-1" data-testid="reg-message" />
      </div>
      <div className="flex gap-2">
        <Button onClick={() => handleRegister(eventId)} className="bg-[#E6B933] hover:bg-[#D4A01F] text-black font-bold flex-1" data-testid="reg-submit">Register</Button>
        <Button variant="outline" onClick={() => { setRegistering(null); setForm({ name: '', email: '', phone: '', message: '' }); }}>Cancel</Button>
      </div>
    </div>
  );

  const EventCard = ({ event, isPast = false, compact = false }) => {
    const isFull = event.capacity && event.registration_count >= event.capacity;
    const isRegistered = submitted[event._id];
    const eventDate = new Date(event.date);

    return (
      <Card className={`overflow-hidden hover:shadow-lg transition-all duration-300 ${isPast ? 'opacity-60' : ''}`} data-testid={`event-card-${event._id}`}>
        {event.image_url && (
          <div className={compact ? 'h-40' : 'h-52'} style={{ overflow: 'hidden' }}>
            <img src={event.image_url} alt={event.title} className="w-full h-full object-cover object-top" />
          </div>
        )}
        <div className="p-5">
          <div className="flex items-center gap-2 mb-2">
            <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase ${event.event_type === 'volunteer' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
              {event.event_type}
            </span>
            {isPast && <span className="px-3 py-1 rounded-full text-xs font-bold uppercase bg-gray-200 text-gray-600">Past</span>}
            {isFull && !isPast && <span className="px-3 py-1 rounded-full text-xs font-bold uppercase bg-red-100 text-red-600">Full</span>}
          </div>

          <h3 className="text-xl font-bold text-gray-900 mb-2">{event.title}</h3>
          {!compact && <p className="text-gray-600 text-sm mb-3 line-clamp-3">{event.description}</p>}

          <div className="space-y-1.5 mb-4 text-sm text-gray-500">
            <div className="flex items-center gap-2"><Calendar className="w-4 h-4 text-[#E6B933]" />{eventDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</div>
            <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-[#E6B933]" />{event.time}</div>
            <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-[#E6B933]" />{event.location}</div>
            {event.capacity && <div className="flex items-center gap-2"><Users className="w-4 h-4 text-[#E6B933]" />{event.registration_count || 0} / {event.capacity} spots filled</div>}
          </div>

          {!isPast && (
            isRegistered ? (
              <div className="flex items-center gap-2 text-green-600 font-semibold" data-testid={`registered-${event._id}`}>
                <CheckCircle className="w-5 h-5" /> You're registered!
              </div>
            ) : registering === event._id ? (
              <RegForm eventId={event._id} />
            ) : (
              <Button
                onClick={() => { setRegistering(event._id); setForm({ name: '', email: '', phone: '', message: '' }); }}
                disabled={isFull}
                className="w-full bg-[#E6B933] hover:bg-[#D4A01F] text-black font-bold"
                data-testid={`register-btn-${event._id}`}
              >
                {isFull ? 'Event Full' : 'Register Now'}
              </Button>
            )
          )}
        </div>
      </Card>
    );
  };

  if (loading) {
    return <div className="min-h-screen pt-20 flex items-center justify-center"><p className="text-lg text-gray-600">Loading events...</p></div>;
  }

  return (
    <div className="min-h-screen pt-20" data-testid="events-page">
      {/* Hero */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#E6B933]/10 rounded-full blur-3xl" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-8">
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
              Upcoming <span className="text-[#E6B933]">Events</span>
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Join us at our community events and volunteer opportunities. Sign up below to reserve your spot.
            </p>
          </div>

          {/* View Toggle */}
          <div className="flex justify-center gap-2" data-testid="view-toggle">
            <Button
              variant={view === 'timeline' ? 'default' : 'outline'}
              onClick={() => setView('timeline')}
              className={view === 'timeline' ? 'bg-[#E6B933] hover:bg-[#D4A01F] text-black' : 'border-gray-300'}
              data-testid="timeline-toggle"
            >
              <List className="w-4 h-4 mr-2" /> Timeline
            </Button>
            <Button
              variant={view === 'calendar' ? 'default' : 'outline'}
              onClick={() => setView('calendar')}
              className={view === 'calendar' ? 'bg-[#E6B933] hover:bg-[#D4A01F] text-black' : 'border-gray-300'}
              data-testid="calendar-toggle"
            >
              <CalendarDays className="w-4 h-4 mr-2" /> Calendar
            </Button>
          </div>
        </div>
      </section>

      {/* Calendar View */}
      {view === 'calendar' && (
        <section className="py-12 bg-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <Card className="p-6 shadow-lg" data-testid="calendar-view">
              {/* Calendar Header */}
              <div className="flex items-center justify-between mb-6">
                <button onClick={prevMonth} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-testid="cal-prev"><ChevronLeft className="w-5 h-5" /></button>
                <h2 className="text-2xl font-bold text-gray-900">{MONTHS[calMonth]} {calYear}</h2>
                <button onClick={nextMonth} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-testid="cal-next"><ChevronRight className="w-5 h-5" /></button>
              </div>

              {/* Day Headers */}
              <div className="grid grid-cols-7 gap-1 mb-2">
                {DAYS.map(d => (
                  <div key={d} className="text-center text-sm font-semibold text-gray-500 py-2">{d}</div>
                ))}
              </div>

              {/* Calendar Grid */}
              <div className="grid grid-cols-7 gap-1">
                {calendarDays().map((day, i) => {
                  if (!day) return <div key={`empty-${i}`} className="h-20" />;
                  const dateStr = formatDateStr(day);
                  const dayEvents = eventsOnDate(dateStr);
                  const isToday = dateStr === new Date().toISOString().split('T')[0];
                  const isSelected = dateStr === selectedDate;
                  const hasEvents = dayEvents.length > 0;

                  return (
                    <button
                      key={day}
                      onClick={() => hasEvents ? setSelectedDate(isSelected ? null : dateStr) : null}
                      className={`h-20 rounded-lg border text-left p-1.5 transition-all duration-200 flex flex-col ${
                        isSelected ? 'border-[#E6B933] bg-[#E6B933]/5 ring-2 ring-[#E6B933]/30' :
                        isToday ? 'border-[#E6B933]/50 bg-[#E6B933]/5' :
                        hasEvents ? 'border-gray-200 hover:border-[#E6B933]/50 hover:bg-[#E6B933]/5 cursor-pointer' :
                        'border-gray-100'
                      }`}
                      data-testid={`cal-day-${day}`}
                    >
                      <span className={`text-sm font-semibold ${isToday ? 'text-[#E6B933]' : 'text-gray-700'}`}>{day}</span>
                      <div className="flex-1 flex flex-col gap-0.5 mt-0.5 overflow-hidden">
                        {dayEvents.slice(0, 2).map((ev, j) => (
                          <div key={j} className={`text-[10px] leading-tight px-1 py-0.5 rounded truncate font-medium ${
                            ev.event_type === 'volunteer' ? 'bg-green-100 text-green-700' : 'bg-[#E6B933]/20 text-[#E6B933]'
                          }`}>{ev.title}</div>
                        ))}
                        {dayEvents.length > 2 && <span className="text-[10px] text-gray-400">+{dayEvents.length - 2} more</span>}
                      </div>
                    </button>
                  );
                })}
              </div>
            </Card>

            {/* Selected Date Events */}
            {selectedDate && selectedEvents.length > 0 && (
              <div className="mt-8 space-y-6" data-testid="selected-date-events">
                <h3 className="text-xl font-bold text-gray-900">
                  Events on {new Date(selectedDate + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                </h3>
                <div className="grid md:grid-cols-2 gap-6">
                  {selectedEvents.map(event => <EventCard key={event._id} event={event} isPast={new Date(event.date) < today} />)}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Timeline View */}
      {view === 'timeline' && (
        <section className="py-12 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {upcoming.length > 0 ? (
              <div className="space-y-8">
                {/* Deduplicate recurring events: show only the next occurrence per title */}
                {(() => {
                  const seen = {};
                  const deduped = [];
                  for (const ev of upcoming) {
                    if (seen[ev.title]) {
                      seen[ev.title].count++;
                    } else {
                      seen[ev.title] = { event: ev, count: 1 };
                      deduped.push(ev.title);
                    }
                  }
                  const dedupedEvents = deduped.map(title => ({ ...seen[title].event, _recurCount: seen[title].count }));

                  return Object.entries(
                    dedupedEvents.reduce((acc, ev) => {
                      const d = new Date(ev.date);
                      const key = `${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
                      if (!acc[key]) acc[key] = [];
                      acc[key].push(ev);
                      return acc;
                    }, {})
                  ).map(([month, monthEvents]) => (
                    <div key={month}>
                      <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-[#E6B933]/10 flex items-center justify-center">
                          <Calendar className="w-5 h-5 text-[#E6B933]" />
                        </div>
                        {month}
                      </h2>
                      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {monthEvents.map(event => (
                          <div key={event._id} className="relative">
                            {event._recurCount > 1 && (
                              <div className="absolute top-3 right-3 z-10 px-3 py-1 rounded-full bg-[#E6B933] text-black text-xs font-bold shadow-md" data-testid={`recurring-badge-${event._id}`}>
                                Every Sunday
                              </div>
                            )}
                            <EventCard event={event} />
                          </div>
                        ))}
                      </div>
                    </div>
                  ));
                })()}
              </div>
            ) : (
              <Card className="p-12 text-center" data-testid="no-events">
                <Heart className="w-12 h-12 text-[#E6B933] mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-2">No Upcoming Events</h3>
                <p className="text-gray-600">Check back soon — we're always planning something new!</p>
              </Card>
            )}
          </div>
        </section>
      )}

      {/* Past Events */}
      {past.length > 0 && (
        <section className="py-12 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Past Events</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {past.map(event => <EventCard key={event._id} event={event} isPast />)}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default Events;
